import { useEffect, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import { useTranslation } from 'react-i18next'
import { SECTION_MODES, useSectionNav } from '../hooks/useSectionNav'

/**
 * Навигация по разделам из нижнего бара виджета.
 *
 * Появилась как единственный быстрый переход между разделами /bots/* после
 * того, как 2026-07-27 убрали ряд иконок-вкладок над панелью (он дублировал
 * сайдбар). На мобиле сайдбар спрятан в бургер, поэтому список тут — не
 * дубль, а основной путь.
 *
 * Живёт отдельным файлом, потому что нужен ДВУМ барам: классическому
 * (GenerateInputBar через mobileModeNav) и tool-режиму (GenerateToolMode) —
 * раньше логика сидела внутри GenerateWidget и второму бару была недоступна.
 */

/**
 * Мобильная кнопка-иконка со списком разделов (десктоп показывает пилюлю с
 * подписью — см. modeNav в GenerateWidget).
 */
export default function WidgetSectionNav() {
	const { t } = useTranslation()
	const { currentCategory, currentMode, switchTo } = useSectionNav()
	const [open, setOpen] = useState(false)
	const triggerRef = useRef<HTMLButtonElement>(null)
	const menuRef = useRef<HTMLDivElement>(null)
	const [pos, setPos] = useState<{ bottom: number; left: number } | null>(null)

	// Координаты пересчитываем при открытии, ресайзе и любом скролле
	// (capture: страница под виджетом прокручивается). Клик мимо и Escape
	// закрывают — до портала список закрывался только повторным нажатием на
	// кнопку, и поверх страницы это читалось бы как «залип».
	useEffect(() => {
		if (!open) return
		const place = () => {
			const el = triggerRef.current
			if (!el) return
			const r = el.getBoundingClientRect()
			setPos({ bottom: window.innerHeight - r.top + 8, left: r.left + r.width / 2 })
		}
		place()
		const onPointerDown = (e: PointerEvent) => {
			const target = e.target as Node
			if (triggerRef.current?.contains(target) || menuRef.current?.contains(target)) return
			setOpen(false)
		}
		const onKey = (e: KeyboardEvent) => {
			if (e.key === 'Escape') setOpen(false)
		}
		window.addEventListener('resize', place)
		window.addEventListener('scroll', place, true)
		document.addEventListener('pointerdown', onPointerDown)
		document.addEventListener('keydown', onKey)
		return () => {
			window.removeEventListener('resize', place)
			window.removeEventListener('scroll', place, true)
			document.removeEventListener('pointerdown', onPointerDown)
			document.removeEventListener('keydown', onKey)
		}
	}, [open])

	return (
		<div className="gw__mode-mobile">
			<button
				ref={triggerRef}
				className={`gw__mode-trigger${open ? ' gw__mode-trigger--open' : ''}`}
				onClick={() => setOpen((v) => !v)}
				type="button"
				aria-haspopup="menu"
				aria-expanded={open}
				aria-label={t(currentMode.labelKey)}
			>
				<img
					src={currentMode.icon}
					alt=""
					className={`gw__mode-trigger__icon${currentMode.colored ? ' gw__mode-trigger__icon--colored' : ''}`}
				/>
			</button>

			{/* ПОРТАЛ, а не потомок бара: панель виджета (.gm / .gtm__bar) —
			    overflow:hidden, и список срезался сверху, как только перестал
			    влезать (вылезло на 4-м пункте). Координаты fixed считаем от
			    кнопки: высота списка больше не зависит от высоты панели, а она
			    плавает (свёрнутый / tool-режим / с результатом). */}
			{open && pos && createPortal(
				<div
					ref={menuRef}
					className="gw__mode-dropdown gw__mode-dropdown--up"
					role="menu"
					style={{ bottom: pos.bottom, left: pos.left }}
				>
					{SECTION_MODES.map((mode) => (
						<button
							key={mode.category}
							className={`gw__mode-dropdown-item${currentCategory === mode.category ? ' gw__mode-dropdown-item--active' : ''}`}
							onClick={() => {
								switchTo(mode.category)
								setOpen(false)
							}}
							type="button"
							role="menuitem"
							title={t(mode.labelKey)}
							aria-label={t(mode.labelKey)}
						>
							<img
								src={mode.icon}
								alt=""
								className={`gw__mode-dropdown-item__icon${mode.colored ? ' gw__mode-dropdown-item__icon--colored' : ''}`}
							/>
						</button>
					))}
				</div>,
				document.body,
			)}
		</div>
	)
}
